import { useCallback, useContext, useState } from "react";
// Types
import {
    FoodCartItemWithUUID,
    IMCartItemWithUUID,
    GiftingLocalCart,
    CartItem,
    IMStructureItem,
    GiftingCartItemType,
    MARKETPLACE_BUSINESS_LINE,
    MARKETPLACE_CATEGORY,
    MARKETPLACE_ID,
    IMGiftBagItem,
} from "@swiggy-private/cart-types";
// Contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
// Utils
import { getBridgeSdk, isEmpty } from "@swiggy-private/checkout-core";
// Helpers
import { getFoodIMItemsListForGiftingCartUpdate } from "@cart-client/helpers/GiftingCart";
import { isFoodItem } from "@cart-client/helpers/GiftingFoodSubCart";
import { isAnyIMItem, getIMGiftBagItemsForUpdateCartAPI } from "@cart-client/helpers/GiftingIMSubCart";

interface UseCartItemActionsProps {
    foodCartItemList: FoodCartItemWithUUID[];
    imCartItemList: IMCartItemWithUUID[];
}

export const useGiftCartItemActions = ({ foodCartItemList, imCartItemList }: UseCartItemActionsProps) => {
    const { doUpdateCart, updateCartInProgress } = useContext(UpdateCartContext);
    const {
        cartState: { localCart },
    } = useContext(CartContext);
    const [updatingItem, setUpdatingItem] = useState<FoodCartItemWithUUID | null>(null);

    /**
     * Filters and updates the cart items list based on the item type
     */
    const getUpdatedCartItemsList = useCallback(
        (item: FoodCartItemWithUUID | IMCartItemWithUUID, itemSubType: GiftingCartItemType) => {
            let updatedCartItemsList: (FoodCartItemWithUUID | IMCartItemWithUUID)[] = [];

            // Filter out the item being updated from the cart list
            if (isFoodItem(itemSubType)) {
                updatedCartItemsList = foodCartItemList.filter(cartItem => cartItem.unId !== item.unId);
            } else if (isAnyIMItem(itemSubType)) {
                updatedCartItemsList = imCartItemList.filter(cartItem => cartItem.unId !== item.unId);
            }

            // Add the item back if its quantity is greater than 0
            if (item.quantity > 0) {
                updatedCartItemsList.push(item);
            }

            // Cast the result to the appropriate type based on itemSubType
            return isFoodItem(itemSubType)
                ? (getFoodIMItemsListForGiftingCartUpdate(
                      updatedCartItemsList as FoodCartItemWithUUID[],
                      itemSubType,
                  ) as FoodCartItemWithUUID[])
                : (getFoodIMItemsListForGiftingCartUpdate(
                      updatedCartItemsList as IMCartItemWithUUID[],
                      itemSubType,
                  ) as IMCartItemWithUUID[]);
        },
        [foodCartItemList, imCartItemList],
    );

    /**
     * Clears the local cart if all items are removed
     */
    const clearCartIfEmpty = useCallback((sortedItemsList: (FoodCartItemWithUUID | IMCartItemWithUUID)[]) => {
        if (isEmpty(sortedItemsList)) {
            getBridgeSdk().clearGiftingLocalCart({
                tenant: {
                    marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.GIFTING,
                    marketplaceCategory: MARKETPLACE_CATEGORY.GIFTING,
                    marketplaceId: MARKETPLACE_ID.SWIGGY,
                },
            });
        }
    }, []);

    /**
     * Prepares the cart update payload based on item type
     */
    const prepareUpdatePayload = useCallback(
        (
            sortedItemsList: (FoodCartItemWithUUID | IMCartItemWithUUID)[],
            itemSubType: GiftingCartItemType,
        ): GiftingLocalCart => {
            let updatePayload: GiftingLocalCart = { ...localCart };

            if (isFoodItem(itemSubType)) {
                updatePayload = {
                    ...updatePayload,
                    foodCartItems: sortedItemsList as FoodCartItemWithUUID[],
                };
            } else if (isAnyIMItem(itemSubType)) {
                updatePayload = {
                    ...updatePayload,
                    imCartItems: sortedItemsList as IMCartItemWithUUID[],
                };
            }

            return updatePayload;
        },
        [localCart],
    );

    /**
     * Updates the quantity of an item in the cart
     * Clears cart if all items are removed
     */
    const onItemUpdate = useCallback(
        async ({
            item,
            itemSubType,
        }: {
            item: FoodCartItemWithUUID | IMCartItemWithUUID;
            itemSubType: GiftingCartItemType;
        }) => {
            const sortedItemsList = getUpdatedCartItemsList(item, itemSubType);
            const updatePayload = prepareUpdatePayload(sortedItemsList, itemSubType);

            if (isEmpty(updatePayload.foodCartItems) && isEmpty(updatePayload.imCartItems)) {
                // clear the cart if all items are removed
                clearCartIfEmpty(sortedItemsList);
            }

            // Update cart with new item list
            if (!updateCartInProgress) {
                await doUpdateCart(updatePayload);
                setUpdatingItem(null);
            }
        },
        [getUpdatedCartItemsList, clearCartIfEmpty, prepareUpdatePayload, updateCartInProgress, doUpdateCart],
    );

    const onRemoveOOSItems = useCallback(
        async ({
            foodItemsToRemove,
            imItemsToRemove,
        }: {
            foodItemsToRemove?: CartItem[];
            imItemsToRemove?: IMStructureItem[];
        }) => {
            // TODO: Implement item removal for gifting cart, descoped for M1
            // if food cart oos items are present, remove them from the cart
            let updatedFoodCartItems: FoodCartItemWithUUID[] = foodCartItemList;
            let updatedImCartItems: IMCartItemWithUUID[] = imCartItemList;
            if (!isEmpty(foodItemsToRemove)) {
                // filter items from  list if id_hash is not present in foodCartOOSItems
                updatedFoodCartItems = foodCartItemList.filter(
                    item => !foodItemsToRemove?.some(oosItem => oosItem.id_hash === item.id_hash),
                );
            }
            // if im cart oos items are present, remove them from the cart
            if (!isEmpty(imItemsToRemove)) {
                // filter items from  list if itemId is not present in imCartOOSItems
                updatedImCartItems = imCartItemList.filter(
                    item => !imItemsToRemove?.some(oosItem => oosItem.itemId === item.itemId),
                );
            }
            // update the cart with the new item list
            if (!updateCartInProgress) {
                const updateCartPayload: GiftingLocalCart = {
                    ...localCart,
                    foodCartItems: getFoodIMItemsListForGiftingCartUpdate(
                        updatedFoodCartItems as FoodCartItemWithUUID[],
                        GiftingCartItemType.FOOD,
                    ) as FoodCartItemWithUUID[],
                    imCartItems: getFoodIMItemsListForGiftingCartUpdate(
                        updatedImCartItems as IMCartItemWithUUID[],
                        GiftingCartItemType.INSTAMART,
                    ) as IMCartItemWithUUID[],
                };

                await doUpdateCart(updateCartPayload);
                setUpdatingItem(null);
            }
        },
        [doUpdateCart, foodCartItemList, imCartItemList, localCart, updateCartInProgress],
    );

    /**
     * Handles item customization including addons and variants
     * Can either update existing item or add a new customized item
     */
    const onItemCustomisation = useCallback(() => {
        // TODO: Implement item customization for gifting cart, descoped for M1
    }, []);

    /**
     * Adds a new gift bag item to the cart
     */
    const addGiftBagItem = useCallback(
        async ({
            imGiftBagItemsToAdd,
            useGiftBagPackaging,
        }: {
            imGiftBagItemsToAdd?: IMGiftBagItem[];
            useGiftBagPackaging?: boolean;
        }) => {
            let updatePayload: GiftingLocalCart = { ...localCart };
            // food or im cart item direct addition is currently not supported
            if (!isEmpty(imGiftBagItemsToAdd)) {
                updatePayload = {
                    ...updatePayload,
                    imCartItems: [
                        ...(updatePayload.imCartItems || []),
                        ...(getIMGiftBagItemsForUpdateCartAPI(imGiftBagItemsToAdd) || []),
                    ],
                    useGiftBagPackaging: useGiftBagPackaging ?? false,
                };
            }
            if (!updateCartInProgress) {
                await doUpdateCart(updatePayload);
                setUpdatingItem(null);
            }
        },
        [doUpdateCart, localCart, updateCartInProgress],
    );

    /**
     * Removes a gift bag item from the cart
     */
    const removeGiftBagItem = useCallback(
        async ({ imGiftBagItemsToRemove }: { imGiftBagItemsToRemove?: { itemId: string }[] }) => {
            const updatePayload: GiftingLocalCart = { ...localCart };
            // food or im cart item direct removal is currently not supported
            if (!isEmpty(imGiftBagItemsToRemove)) {
                imGiftBagItemsToRemove?.forEach(removeGiftBags => {
                    updatePayload.imCartItems = updatePayload.imCartItems?.filter(
                        imItem => imItem.itemId !== removeGiftBags.itemId,
                    );
                });
            }
            if (!updateCartInProgress) {
                await doUpdateCart(updatePayload);
                setUpdatingItem(null);
            }
        },
        [doUpdateCart, localCart, updateCartInProgress],
    );

    return {
        onItemUpdate,
        onItemCustomisation,
        addGiftBagItem,
        updateCartInProgress,
        updatingItem,
        setUpdatingItem,
        onRemoveOOSItems,
        removeGiftBagItem,
    };
};
